import { useState, useCallback } from 'react'
import {
  ArrowRight, Calendar, Clock, Phone, Mail, Sparkles, ExternalLink,
  Calculator, FileText, BookOpen, Building2, User, GraduationCap, Search, HeartPulse,
  ClipboardList, Users, ShieldAlert, Linkedin, Play, AtSign, Rss,
  Briefcase, Scale, Landmark, GlobeLock,
} from 'lucide-react'

const TOOL_ICONS = {
  calculator: Calculator, 'file-text': FileText, 'book-open': BookOpen, 'building-2': Building2,
  user: User, 'graduation-cap': GraduationCap, search: Search, 'heart-pulse': HeartPulse,
  'clipboard-list': ClipboardList, gavel: FileText, users: Users, 'shield-alert': ShieldAlert,
  briefcase: Briefcase, scale: Scale, landmark: Landmark, 'globe-lock': GlobeLock,
}

const PROFIL_OPTIONS = [
  { value: 'elu', label: 'Elu local' },
  { value: 'agent', label: 'Agent territorial' },
  { value: 'drh', label: 'DRH / DGS / RRH' },
  { value: 'candidat', label: 'Candidat FPT' },
  { value: 'citoyen', label: 'Grand public' },
]

const TAGS_BY_PROFIL = {
  elu: [
    { id: 'statut-elu', label: "Statut de l'elu", icon: 'landmark' },
    { id: 'indemnites', label: 'Indemnites et regime', icon: 'calculator' },
    { id: 'instances', label: 'Instances paritaires', icon: 'users' },
    { id: 'recrutement', label: 'Recruter un agent', icon: 'user' },
    { id: 'obligations', label: 'Obligations employeur', icon: 'shield-alert' },
  ],
  agent: [
    { id: 'carriere', label: 'Ma carriere', icon: 'user' },
    { id: 'concours', label: 'Concours et examens', icon: 'graduation-cap' },
    { id: 'formation', label: 'Formations', icon: 'book-open' },
    { id: 'sante', label: 'Sante au travail', icon: 'heart-pulse' },
    { id: 'mobilite', label: 'Mobilite et mutation', icon: 'building-2' },
    { id: 'retraite', label: 'Retraite', icon: 'clipboard-list' },
  ],
  drh: [
    { id: 'gestion-stat', label: 'Gestion statutaire', icon: 'file-text' },
    { id: 'concours-orga', label: 'Organiser un concours', icon: 'clipboard-list' },
    { id: 'paie', label: 'Paie et charges', icon: 'calculator' },
    { id: 'sante-prev', label: 'Prevention et sante', icon: 'heart-pulse' },
    { id: 'instances-drh', label: 'CAP / CST / CCP', icon: 'users' },
    { id: 'rupture', label: 'Rupture conventionnelle', icon: 'scale' },
  ],
  candidat: [
    { id: 'concours-fpt', label: 'Concours de la FPT', icon: 'graduation-cap' },
    { id: 'emploi', label: "Offres d'emploi", icon: 'briefcase' },
    { id: 'metiers', label: 'Metiers territoriaux', icon: 'building-2' },
    { id: 'preparation', label: 'Se preparer', icon: 'book-open' },
    { id: 'inscription', label: "S'inscrire", icon: 'clipboard-list' },
  ],
  citoyen: [
    { id: 'missions', label: 'Missions du CDG31', icon: 'landmark' },
    { id: 'acces-emploi', label: 'Travailler dans le public', icon: 'briefcase' },
    { id: 'contact', label: 'Nous contacter', icon: 'user' },
    { id: 'transparence', label: 'Acces aux documents', icon: 'globe-lock' },
  ],
}

const RESOURCES_BY_TAG = {
  'statut-elu': {
    moreUrl: 'https://www.cdg31.fr/thematiques/elus',
    items: [
      { title: 'Regime indemnitaire des elus', desc: 'Indemnites de fonction, remboursement de frais', url: 'https://www.cdg31.fr/thematiques/elus' },
      { title: 'Protection sociale', desc: "Couverture sociale et responsabilite de l'elu", url: 'https://www.cdg31.fr/thematiques/elus' },
      { title: 'Formation des elus', desc: 'Droit a la formation et organismes agrees', url: 'https://www.cdg31.fr/thematiques/formation' },
    ],
  },
  indemnites: {
    moreUrl: 'https://www.cdg31.fr/thematiques/elus',
    items: [
      { title: 'Bareme des indemnites 2026', desc: 'Montants selon la taille de la collectivite', url: 'https://www.cdg31.fr/thematiques/elus' },
      { title: 'Regime fiscal', desc: 'Imposition et cotisations sociales des elus', url: 'https://www.cdg31.fr/thematiques/elus' },
    ],
  },
  instances: {
    moreUrl: 'https://www.cdg31.fr/thematiques/instances-paritaires',
    items: [
      { title: 'Comite Social Territorial (CST)', desc: 'Composition, attributions et fonctionnement', url: 'https://www.cdg31.fr/thematiques/instances-paritaires' },
      { title: 'Commission Administrative Paritaire', desc: 'CAP : role, saisine et calendrier', url: 'https://www.cdg31.fr/thematiques/instances-paritaires' },
      { title: 'Commission de reforme', desc: 'Accidents de service, maladies professionnelles', url: 'https://www.cdg31.fr/thematiques/instances-paritaires' },
    ],
  },
  recrutement: {
    moreUrl: 'https://www.cdg31.fr/thematiques/recrutement',
    items: [
      { title: "Publier une offre d'emploi", desc: 'Deposez vos offres sur le portail FPT', url: 'https://www.cdg31.fr/thematiques/recrutement' },
      { title: 'Recruter un contractuel', desc: 'Cadre juridique, duree et renouvellement', url: 'https://www.cdg31.fr/thematiques/recrutement' },
      { title: 'Remplacement et interim', desc: 'Missions temporaires et centre de gestion', url: 'https://www.cdg31.fr/thematiques/recrutement' },
    ],
  },
  obligations: {
    moreUrl: 'https://www.cdg31.fr/thematiques/gestion-statutaire',
    items: [
      { title: 'Obligations de l\'employeur territorial', desc: 'Sante, securite et conditions de travail', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
      { title: 'Document unique (DUERP)', desc: 'Evaluation des risques professionnels', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
    ],
  },
  carriere: {
    moreUrl: 'https://www.cdg31.fr/thematiques/carrieres',
    items: [
      { title: 'Avancement de grade', desc: 'Conditions, ratios et tableau annuel', url: 'https://www.cdg31.fr/thematiques/carrieres' },
      { title: 'Promotion interne', desc: 'Acces aux cadres d\'emplois superieurs', url: 'https://www.cdg31.fr/thematiques/carrieres' },
      { title: 'Entretien professionnel', desc: 'Preparation et compte rendu annuel', url: 'https://www.cdg31.fr/thematiques/carrieres' },
    ],
  },
  concours: {
    moreUrl: 'https://www.cdg31.fr/concours-examens',
    items: [
      { title: 'Calendrier des concours 2026', desc: 'Dates, inscriptions et epreuves', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Conditions d\'acces', desc: 'Categories A, B, C et filieres', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Resultats et listes d\'aptitude', desc: 'Consultez les resultats publies', url: 'https://www.cdg31.fr/concours-examens' },
    ],
  },
  formation: {
    moreUrl: 'https://www.cdg31.fr/thematiques/formation',
    items: [
      { title: 'Catalogue des formations', desc: 'Formations obligatoires et facultatives', url: 'https://www.cdg31.fr/thematiques/formation' },
      { title: 'Preparation aux concours', desc: 'Sessions de preparation et accompagnement', url: 'https://www.cdg31.fr/thematiques/formation' },
    ],
  },
  sante: {
    moreUrl: 'https://www.cdg31.fr/thematiques/sante-travail',
    items: [
      { title: 'Medecine preventive', desc: 'Visites medicales et suivi des agents', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
      { title: 'Risques psychosociaux', desc: 'Prevention et accompagnement', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
      { title: 'Maintien dans l\'emploi', desc: 'Amenagements et reclassement', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
    ],
  },
  mobilite: {
    moreUrl: 'https://www.cdg31.fr/thematiques/carrieres',
    items: [
      { title: 'Mutation et detachement', desc: 'Procedures de mobilite entre collectivites', url: 'https://www.cdg31.fr/thematiques/carrieres' },
      { title: 'Mise a disposition', desc: 'Conditions et convention', url: 'https://www.cdg31.fr/thematiques/carrieres' },
    ],
  },
  retraite: {
    moreUrl: 'https://www.cdg31.fr/thematiques/retraite',
    items: [
      { title: 'Droit a pension CNRACL', desc: 'Conditions, calcul et demarches', url: 'https://www.cdg31.fr/thematiques/retraite' },
      { title: 'Retraite progressive', desc: 'Amenager sa fin de carriere', url: 'https://www.cdg31.fr/thematiques/retraite' },
    ],
  },
  'gestion-stat': {
    moreUrl: 'https://www.cdg31.fr/thematiques/gestion-statutaire',
    items: [
      { title: 'Positions administratives', desc: 'Activite, detachement, disponibilite, conge parental', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
      { title: 'Modeles d\'arretes', desc: 'Arretes et deliberations prets a personnaliser', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
      { title: 'Fiches pratiques', desc: 'Toutes les fiches statutaires expliquees', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
    ],
  },
  'concours-orga': {
    moreUrl: 'https://www.cdg31.fr/concours-examens',
    items: [
      { title: 'Organiser un concours', desc: 'Procedure, calendrier et logistique', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Examens professionnels', desc: 'Organisation et conditions d\'acces', url: 'https://www.cdg31.fr/concours-examens' },
    ],
  },
  paie: {
    moreUrl: 'https://www.cdg31.fr/thematiques/gestion-statutaire',
    items: [
      { title: 'Simulateur de remuneration', desc: 'Calculez les couts employeur', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
      { title: 'Charges et cotisations', desc: 'Taux en vigueur et declarations', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
    ],
  },
  'sante-prev': {
    moreUrl: 'https://www.cdg31.fr/thematiques/sante-travail',
    items: [
      { title: 'Service de medecine preventive', desc: 'Adhesion et prestations du CDG31', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
      { title: 'Accompagnement RPS', desc: 'Diagnostic, mediation et plan d\'action', url: 'https://www.cdg31.fr/thematiques/sante-travail' },
    ],
  },
  'instances-drh': {
    moreUrl: 'https://www.cdg31.fr/thematiques/instances-paritaires',
    items: [
      { title: 'CAP — saisine et calendrier', desc: 'Dossiers et dates des prochaines seances', url: 'https://www.cdg31.fr/thematiques/instances-paritaires' },
      { title: 'CST et CCP', desc: 'Composition, fonctionnement, PV', url: 'https://www.cdg31.fr/thematiques/instances-paritaires' },
    ],
  },
  rupture: {
    moreUrl: 'https://www.cdg31.fr/thematiques/gestion-statutaire',
    items: [
      { title: 'Rupture conventionnelle', desc: 'Procedure, indemnite et simulateur', url: 'https://www.cdg31.fr/thematiques/gestion-statutaire' },
      { title: 'Simulateur d\'indemnite', desc: 'Calculez le montant min/max', url: '/cdg31/simulateur-rupture-conventionnelle' },
    ],
  },
  'concours-fpt': {
    moreUrl: 'https://www.cdg31.fr/concours-examens',
    items: [
      { title: 'Calendrier regional 2026', desc: 'Tous les concours ouverts en Occitanie', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Filieres et categories', desc: 'Administrative, technique, medico-sociale...', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Se preparer aux epreuves', desc: 'Ressources et conseils pratiques', url: 'https://www.cdg31.fr/thematiques/formation' },
    ],
  },
  emploi: {
    moreUrl: 'https://www.cdg31.fr/thematiques/recrutement',
    items: [
      { title: 'Offres d\'emploi territorial', desc: 'Postes a pourvoir en Haute-Garonne', url: 'https://www.cdg31.fr/thematiques/recrutement' },
      { title: 'Bourse de l\'emploi', desc: 'Declarations de vacance de poste', url: 'https://www.cdg31.fr/thematiques/recrutement' },
    ],
  },
  metiers: {
    moreUrl: 'https://www.cdg31.fr/thematiques/recrutement',
    items: [
      { title: 'Repertoire des metiers', desc: 'Decouvrir les 250+ metiers de la FPT', url: 'https://www.cdg31.fr/thematiques/recrutement' },
      { title: 'Temoignages d\'agents', desc: 'Parcours et retours d\'experience', url: 'https://www.cdg31.fr/thematiques/recrutement' },
    ],
  },
  preparation: {
    moreUrl: 'https://www.cdg31.fr/thematiques/formation',
    items: [
      { title: 'Preparation aux concours', desc: 'Sessions et supports pedagogiques', url: 'https://www.cdg31.fr/thematiques/formation' },
      { title: 'Epreuves-types', desc: 'Annales et corriges', url: 'https://www.cdg31.fr/concours-examens' },
    ],
  },
  inscription: {
    moreUrl: 'https://www.cdg31.fr/concours-examens',
    items: [
      { title: 'S\'inscrire a un concours', desc: 'Demarche en ligne, pieces a fournir', url: 'https://www.cdg31.fr/concours-examens' },
      { title: 'Conditions d\'inscription', desc: 'Diplomes, experience et equivalences', url: 'https://www.cdg31.fr/concours-examens' },
    ],
  },
  missions: {
    moreUrl: 'https://www.cdg31.fr/presentation',
    items: [
      { title: 'Presentation du CDG31', desc: 'Missions, organisation et gouvernance', url: 'https://www.cdg31.fr/presentation' },
      { title: 'Rapport d\'activite', desc: 'Bilan annuel et chiffres cles', url: 'https://www.cdg31.fr/presentation' },
    ],
  },
  'acces-emploi': {
    moreUrl: 'https://www.cdg31.fr/thematiques/recrutement',
    items: [
      { title: 'Travailler dans la fonction publique', desc: 'Voies d\'acces et conditions generales', url: 'https://www.cdg31.fr/thematiques/recrutement' },
      { title: 'Apprentissage et service civique', desc: 'Offres pour les jeunes', url: 'https://www.cdg31.fr/thematiques/recrutement' },
    ],
  },
  contact: {
    moreUrl: 'https://www.cdg31.fr/contact',
    items: [
      { title: 'Coordonnees et horaires', desc: '590 rue Buissonniere, Labege — 05 81 91 93 00', url: 'https://www.cdg31.fr/contact' },
      { title: 'Formulaire de contact', desc: 'Envoyez votre demande en ligne', url: 'https://www.cdg31.fr/contact' },
    ],
  },
  transparence: {
    moreUrl: 'https://www.cdg31.fr/presentation',
    items: [
      { title: 'Documents administratifs', desc: 'Deliberations, PV et rapports publics', url: 'https://www.cdg31.fr/presentation' },
      { title: 'Donnees ouvertes', desc: 'Acces aux jeux de donnees du CDG31', url: 'https://www.cdg31.fr/presentation' },
    ],
  },
}

export function JeSuisJeVeux({ onProfileChange, onAskAI }) {
  const [profil, setProfil] = useState('elu')
  const [activeTag, setActiveTag] = useState(null)

  const handleProfilChange = useCallback((value) => {
    setProfil(value)
    setActiveTag(null)
    onProfileChange?.(value)
  }, [onProfileChange])

  const handleTagClick = useCallback((tagId) => {
    setActiveTag(prev => prev === tagId ? null : tagId)
  }, [])

  const handleAskAI = useCallback((tagLabel) => {
    const profilLabel = PROFIL_OPTIONS.find(p => p.value === profil)?.label || profil
    const message = `En tant que ${profilLabel}, j'ai besoin d'informations sur : ${tagLabel}`
    onAskAI?.(message)
  }, [profil, onAskAI])

  const tags = TAGS_BY_PROFIL[profil] || []
  const resources = activeTag ? RESOURCES_BY_TAG[activeTag] : null
  const activeTagLabel = tags.find(t => t.id === activeTag)?.label || ''

  return (
    <section className="cdg-jsjv">
      <div className="cdg-jsjv-bg-pattern" />
      <div className="cdg-container">
        <div className="cdg-jsjv-head">
          <span className="cdg-eyebrow">Trouvez votre information</span>
          <h2>Comment pouvons-nous vous aider&nbsp;?</h2>
          <p className="cdg-jsjv-subtitle">Selectionnez votre profil, puis explorez les ressources adaptees a votre situation.</p>
        </div>

        <div className="cdg-jsjv-card">
          <div className="cdg-jsjv-profils-row">
            <span className="cdg-jsjv-profils-label">Je suis</span>
            <div className="cdg-jsjv-profils">
              {PROFIL_OPTIONS.map(o => (
                <button
                  key={o.value}
                  className={`cdg-jsjv-profil-pill${profil === o.value ? ' active' : ''}`}
                  onClick={() => handleProfilChange(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cdg-jsjv-divider" />

          <div className="cdg-jsjv-tags" key={profil}>
            {tags.map((tag, i) => {
              const Icon = TOOL_ICONS[tag.icon] || FileText
              return (
                <button
                  key={tag.id}
                  className={`cdg-jsjv-tag${activeTag === tag.id ? ' active' : ''}`}
                  onClick={() => handleTagClick(tag.id)}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <span className="cdg-jsjv-tag-icon"><Icon size={14} /></span>
                  {tag.label}
                </button>
              )
            })}
          </div>

          {resources && (
            <div className="cdg-jsjv-listing" key={activeTag}>
              <div className="cdg-jsjv-listing-items">
                {resources.items.map((item, i) => (
                  <a
                    key={i}
                    className="cdg-jsjv-listing-item"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <span className="cdg-jsjv-listing-num">{i + 1}</span>
                    <div className="cdg-jsjv-listing-item-text">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                    <ExternalLink size={14} className="cdg-jsjv-listing-item-arrow" />
                  </a>
                ))}
              </div>
              <div className="cdg-jsjv-listing-footer">
                <a
                  className="cdg-btn cdg-btn-outline"
                  href={resources.moreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Plus de ressources
                  <ArrowRight size={14} />
                </a>
                <button
                  className="cdg-btn cdg-btn-ai"
                  onClick={() => handleAskAI(activeTagLabel)}
                >
                  <span className="cdg-btn-ai-sparkle"><Sparkles size={14} /></span>
                  Demander a l'IA
                </button>
              </div>
            </div>
          )}

          {!resources && (
            <div className="cdg-jsjv-hint">
              <div className="cdg-jsjv-hint-icon">
                <Search size={28} />
              </div>
              <p>Choisissez un sujet ci-dessus pour decouvrir les ressources</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export function Actualites() {
  return (
    <section id="actualites" className="cdg-actu">
      <div className="cdg-container">
        <div className="cdg-actu-grid">
          <div>
            <div className="cdg-sec-head">
              <div>
                <span className="cdg-eyebrow">Actualites</span>
                <h2 style={{ marginTop: '.5rem' }}>A la une du CDG31</h2>
              </div>
              <a className="cdg-link-inline" href="#">Toutes les actualites<ArrowRight size={14} /></a>
            </div>
            <div className="cdg-actu-cards">
              <article className="cdg-card cdg-card-feature">
                <div className="cdg-card-img">
                  <img src="/assets/cdg31/visuels/actualites/emploi_territorial.jpg" alt="" />
                  <span className="cdg-badge cdg-badge-red">Carrieres</span>
                </div>
                <div className="cdg-card-body">
                  <h3>Loi de finances 2026&nbsp;: les evolutions qui concernent les agents publics territoriaux</h3>
                  <p className="cdg-card-sub">Le CDG31 fait le point sur les nouvelles dispositions issues de la loi n°2026-103 du 19 fevrier 2026 et leurs impacts concrets pour les employeurs publics.</p>
                  <div className="cdg-card-meta">
                    <span><Calendar size={12} />27 fevrier 2026</span>
                    <span><Clock size={12} />Lecture 5 min</span>
                  </div>
                </div>
              </article>
              <article className="cdg-card">
                <div className="cdg-card-img">
                  <img src="/assets/cdg31/visuels/actualites/concours.jpg" alt="" />
                  <span className="cdg-badge cdg-badge-teal">Concours</span>
                </div>
                <div className="cdg-card-body">
                  <h3>Ouverture des inscriptions — Concours d'assistant socio-educatif</h3>
                  <div className="cdg-card-meta"><span><Calendar size={12} />15 avril 2026</span></div>
                </div>
              </article>
              <article className="cdg-card">
                <div className="cdg-card-img">
                  <img src="/assets/cdg31/visuels/actualites/juridique.jpg" alt="" />
                  <span className="cdg-badge cdg-badge-violet">Juridique</span>
                </div>
                <div className="cdg-card-body">
                  <h3>Rupture conventionnelle&nbsp;: le CDG-INFO 2026-02 est disponible</h3>
                  <div className="cdg-card-meta"><span><Calendar size={12} />10 avril 2026</span></div>
                </div>
              </article>
            </div>
          </div>
          <div id="agenda">
            <div className="cdg-sec-head">
              <div>
                <span className="cdg-eyebrow">Agenda</span>
                <h2 style={{ marginTop: '.5rem', fontSize: 'var(--fs-2xl)' }}>Prochains evenements</h2>
              </div>
            </div>
            <div className="cdg-agenda">
              {[
                ['21', 'AVR', 'Webinaire', 'Forum emploi territorial — Toulouse', 'teal', '09h00 · En ligne'],
                ['24', 'AVR', 'Formation', 'Mardis du statut — Rupture conventionnelle', 'violet', '14h00 · Labege'],
                ['29', 'AVR', 'Accompagnement', 'Conseil en evolution professionnelle (CEP)', 'teal', 'Sur rendez-vous'],
                ['18', 'JUN', 'Reunion', "Reunion d'information mobilite interne", 'red', '10h00 · Labege'],
              ].map(([d, m, k, t, c, meta]) => (
                <a key={t} className="cdg-ev" href="#">
                  <div className="cdg-ev-date">
                    <p className="d">{d}</p>
                    <p className="m">{m}</p>
                  </div>
                  <div className="cdg-ev-body">
                    <span className={`cdg-ev-k ${c}`}>{k}</span>
                    <p className="cdg-ev-t">{t}</p>
                    <p className="cdg-ev-meta">{meta}</p>
                  </div>
                </a>
              ))}
            </div>
            <a className="cdg-link-inline" href="#" style={{ marginTop: '1rem', display: 'inline-flex' }}>Voir tout l'agenda<ArrowRight size={14} /></a>
          </div>
        </div>
      </div>
    </section>
  )
}

export function Toolbox() {
  const [tab, setTab] = useState('elus')
  const byTab = {
    elus: [
      ['calculator', 'Simulateurs', 'Simulateur de remuneration et de carriere', 'Outil'],
      ['file-text', "Modeles d'arretes", 'Arretes et deliberations prets a personnaliser', 'Modele'],
      ['book-open', 'Fiches pratiques', 'Toutes les fiches statutaires expliquees', 'Guide'],
      ['building-2', 'Portail emploi FPT', "Deposez vos offres d'emploi territorial", 'Portail'],
    ],
    agents: [
      ['user', 'Mon dossier', 'Consulter ma fiche de carriere', 'Espace'],
      ['graduation-cap', 'Catalogue formations', 'Toutes les formations du CDG31', 'Catalogue'],
      ['search', "Offres d'emploi", 'Postes a pourvoir en Haute-Garonne', 'Emploi'],
      ['heart-pulse', 'Sante au travail', 'Prendre rendez-vous avec la medecine', 'Service'],
    ],
    drh: [
      ['clipboard-list', 'Organiser un concours', 'Procedure et calendrier', 'Guide'],
      ['gavel', 'Circulaires', 'Dernieres circulaires et textes', 'Documentation'],
      ['users', 'Instances paritaires', 'CAP, CST, commission reforme', 'Service'],
      ['shield-alert', 'Prevention des risques', "DUERP et plans d'action", 'Outil'],
    ],
  }

  return (
    <section className="cdg-toolbox">
      <div className="cdg-container">
        <div className="cdg-center">
          <span className="cdg-eyebrow">Boite a outils</span>
          <h2 style={{ marginTop: '.75rem' }}>Vos outils en acces direct</h2>
          <p>Fiches pratiques, simulateurs, modeles de documents — disponibles sans connexion, adaptes a votre profil.</p>
        </div>
        <div className="cdg-toolbox-tabs" role="tablist">
          {[['elus', 'Elus & DGS'], ['agents', 'Agents'], ['drh', 'DRH / RRH']].map(([id, l]) => (
            <button key={id} className={`cdg-tab-pill ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{l}</button>
          ))}
        </div>
        <div className="cdg-toolbox-grid">
          {byTab[tab].map(([ic, t, s, tag]) => {
            const Icon = TOOL_ICONS[ic] || FileText
            return (
              <a key={t} className="cdg-tool" href="#">
                <div className="cdg-tool-head">
                  <div className="cdg-tool-ic"><Icon size={22} /></div>
                  <span className="cdg-tool-tag">{tag}</span>
                </div>
                <h3>{t}</h3>
                <p>{s}</p>
                <span className="cdg-tool-link">Acceder</span>
              </a>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export function ContactBand() {
  return (
    <section className="cdg-contact-band">
      <div className="cdg-container">
        <div>
          <span className="cdg-eyebrow">Nous contacter</span>
          <h2>Une question&nbsp;? Nos conseillers RH vous repondent.</h2>
          <p>Accueil telephonique du lundi au vendredi, 9h-12h30 et 13h30-17h. Permanences juridiques sur rendez-vous.</p>
        </div>
        <div className="cdg-contact-btns">
          <a className="cdg-btn cdg-btn-white cdg-btn-lg" href="tel:0581919300"><Phone size={16} />05 81 91 93 00</a>
          <a className="cdg-btn cdg-btn-white-out cdg-btn-lg" href="#"><Mail size={16} />Formulaire de contact</a>
        </div>
      </div>
    </section>
  )
}

export function Footer() {
  const cols = [
    ['Nos services', ['Recrutement & emploi', 'Formation professionnelle', 'Sante & prevention', 'Conseil statutaire', 'Instances paritaires', 'Concours']],
    ['Ressources', ['Fiches pratiques', 'Modeles de documents', 'Circulaires & textes', 'Simulateurs RH', 'Portail emploi', 'CDG-INFO']],
    ['Le CDG31', ['Presentation', 'Gouvernance', "Projet d'etablissement", 'Mentions legales', 'Politique RGPD', 'Accessibilite']],
  ]

  return (
    <footer className="cdg-footer">
      <div className="cdg-container">
        <div className="cdg-footer-grid">
          <div>
            <img src="/assets/cdg31/logos/CDG31_1_blanc.png" alt="CDG31" className="cdg-footer-logo" />
            <p className="cdg-footer-addr"><strong>Centre de Gestion de la Fonction Publique Territoriale de la Haute-Garonne</strong></p>
            <p className="cdg-footer-addr">590 rue Buissonniere<br />CS 37666<br />31676 Labege Cedex</p>
            <div className="cdg-footer-social">
              <a aria-label="LinkedIn" href="#"><Linkedin size={16} /></a>
              <a aria-label="YouTube" href="#"><Play size={16} /></a>
              <a aria-label="X / Twitter" href="#"><AtSign size={16} /></a>
              <a aria-label="RSS" href="#"><Rss size={16} /></a>
            </div>
          </div>
          {cols.map(([h, items]) => (
            <div key={h}>
              <h3>{h}</h3>
              <ul>{items.map(i => <li key={i}><a href="#">{i}</a></li>)}</ul>
            </div>
          ))}
        </div>
        <div className="cdg-footer-bottom">
          <p>&copy; 2026 CDG31 — Centre de Gestion de la FPT de la Haute-Garonne. Tous droits reserves.</p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <a href="#">Accessibilite : partiellement conforme</a>
            <a href="#">Plan du site</a>
            <a href="#">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  )
}
